import { Copy, Link2, LoaderCircle, Mail, ShieldCheck, UserPlus, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { extractErrorMessage, fetchCurrentUser, fetchUsers, generateManagedUserResetLink, inviteUser, sendUserPasswordReset, updateManagedUser } from "../lib/api";
import { buildAppUrl } from "../lib/url";
import { PermissionsChecklist } from "../components/users/PermissionsChecklist";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const statusStyles = {
  Active: "border-emerald-200 bg-emerald-50 text-emerald-700",
  Invited: "border-amber-200 bg-amber-50 text-amber-700",
  Disabled: "border-zinc-200 bg-zinc-100 text-zinc-700",
};

const ROLE_DEFAULT_PERMISSIONS = {
  Admin: [],
  Dispatcher: ["dashboard", "customers", "records", "loads", "dispatch_board", "planning_board", "invoices", "reports", "settings"],
  Accounting: ["dashboard", "customers", "invoices", "reports"],
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "—");
const RESET_PASSWORD_URL = buildAppUrl("/reset-password");

const SummaryCard = ({ label, value, testId }) => (
  <div className="rounded-[28px] border border-zinc-200 bg-zinc-50 px-5 py-5" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">{label}</p>
    <p className="mt-3 text-3xl font-semibold text-zinc-900">{value}</p>
  </div>
);

export default function UsersPage() {
  const [currentUser, setCurrentUser] = useState(null);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total_users: 0, admins: 0, dispatchers: 0, accountants: 0, invited: 0, disabled: 0 });
  const [loading, setLoading] = useState(true);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [rowSavingId, setRowSavingId] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [permissionCatalog, setPermissionCatalog] = useState([]);
  const [inviteForm, setInviteForm] = useState({ full_name: "", email: "", role: "Dispatcher", permissions: ROLE_DEFAULT_PERMISSIONS.Dispatcher });
  const [manualLinkState, setManualLinkState] = useState({ email: "", label: "", link: "" });
  const [accessDialog, setAccessDialog] = useState({ open: false, user: null, permissions: [] });

  const presentManualLink = async (payload, email, label) => {
    if (!payload?.manual_link) {
      return;
    }
    setManualLinkState({ email, label, link: payload.manual_link });
    try {
      await navigator.clipboard.writeText(payload.manual_link);
      toast.success(`${label} copied for ${email}.`);
    } catch (error) {
      toast.success(payload.message || `${label} ready below.`);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const [me, payload] = await Promise.all([fetchCurrentUser(), fetchUsers()]);
      setCurrentUser(me);
      setItems(payload.items || []);
      setSummary(payload.summary || { total_users: 0, admins: 0, dispatchers: 0, accountants: 0, invited: 0, disabled: 0 });
      setPermissionCatalog(payload.permission_catalog || []);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to load user management."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const visibleUsers = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch = !search.trim() || [item.name, item.email, item.role, item.auth_status].filter(Boolean).join(" ").toLowerCase().includes(search.trim().toLowerCase());
      const matchesStatus = statusFilter === "all" || item.auth_status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [items, search, statusFilter]);

  const handleInviteSubmit = async (event) => {
    event.preventDefault();
    setInviteSaving(true);
    try {
      const result = await inviteUser({ ...inviteForm, permissions: inviteForm.role === "Admin" ? null : inviteForm.permissions, redirect_to: RESET_PASSWORD_URL });
      if (result.manual_link) {
        await presentManualLink(result, inviteForm.email, "Setup link");
      } else {
        toast.success(`Invite sent to ${inviteForm.email}.`);
      }
      setInviteForm({ full_name: "", email: "", role: "Dispatcher", permissions: ROLE_DEFAULT_PERMISSIONS.Dispatcher });
      loadUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to send this invite."));
    } finally {
      setInviteSaving(false);
    }
  };

  const handleRoleChange = async (user, role) => {
    setRowSavingId(user.id);
    try {
      const updated = await updateManagedUser(user.id, { full_name: user.full_name || user.name, role, active_status: user.active_status });
      toast.success(`${updated.name} is now ${updated.role}.`);
      loadUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to update that role."));
    } finally {
      setRowSavingId("");
    }
  };

  const handleToggleActive = async (user) => {
    setRowSavingId(user.id);
    try {
      const updated = await updateManagedUser(user.id, { full_name: user.full_name || user.name, role: user.role, active_status: !user.active_status });
      toast.success(updated.active_status ? `${updated.name} can access the app again.` : `${updated.name} has been disabled.`);
      loadUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to update that access setting."));
    } finally {
      setRowSavingId("");
    }
  };

  const openAccessDialog = (user) => {
    setAccessDialog({ open: true, user, permissions: user.effective_permissions || ROLE_DEFAULT_PERMISSIONS[user.role] || [] });
  };

  const handleSaveAccess = async () => {
    const { user, permissions } = accessDialog;
    setRowSavingId(user.id);
    try {
      const updated = await updateManagedUser(user.id, { full_name: user.full_name || user.name, role: user.role, active_status: user.active_status, permissions });
      toast.success(`Access updated for ${updated.name}.`);
      setAccessDialog({ open: false, user: null, permissions: [] });
      loadUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to update access permissions."));
    } finally {
      setRowSavingId("");
    }
  };


  const handleSendReset = async (user) => {
    setRowSavingId(user.id);
    try {
      const result = await sendUserPasswordReset(user.id, { redirect_to: RESET_PASSWORD_URL });
      if (result.manual_link) {
        await presentManualLink(result, user.email, user.auth_status === "Invited" ? "Setup link" : "Reset link");
      } else {
        toast.success(result.message || `Password reset email sent to ${user.email}.`);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to send a password reset email."));
    } finally {
      setRowSavingId("");
    }
  };

  const handleGenerateManualLink = async (user) => {
    setRowSavingId(user.id);
    try {
      const result = await generateManagedUserResetLink(user.id, { redirect_to: RESET_PASSWORD_URL });
      await presentManualLink(result, user.email, user.auth_status === "Invited" ? "Setup link" : "Reset link");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to generate a manual reset link."));
    } finally {
      setRowSavingId("");
    }
  };

  const handleCopyVisibleManualLink = async () => {
    if (!manualLinkState.link) {
      return;
    }
    try {
      await navigator.clipboard.writeText(manualLinkState.link);
      toast.success(`${manualLinkState.label} copied.`);
    } catch (error) {
      toast.error("Unable to copy the link automatically. Please copy it manually.");
    }
  };

  if (!loading && currentUser?.role !== "Admin") {
    return (
      <div className="space-y-6" data-testid="users-page-access-denied">
        <section>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Admin only</p>
          <h1 className="mt-2 text-4xl font-semibold text-zinc-900">Users</h1>
        </section>
        <div className="panel-surface rounded-[32px] px-6 py-8" data-testid="users-access-denied-card">
          <p className="text-lg font-semibold text-zinc-900">You need Admin access to manage office users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Office access</p>
          <h1 className="mt-2 text-4xl font-semibold text-zinc-900">Users</h1>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5" data-testid="users-summary-grid">
        <SummaryCard label="Total users" testId="users-summary-total" value={summary.total_users} />
        <SummaryCard label="Admins" testId="users-summary-admins" value={summary.admins} />
        <SummaryCard label="Dispatchers" testId="users-summary-dispatchers" value={summary.dispatchers} />
        <SummaryCard label="Accountants" testId="users-summary-accountants" value={summary.accountants} />
        <SummaryCard label="Invited" testId="users-summary-invited" value={summary.invited} />
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <form className="panel-surface space-y-4 px-6 py-6" data-testid="users-invite-form" onSubmit={handleInviteSubmit}>
          <div className="flex items-start gap-3">
            <div className="rounded-2xl bg-zinc-100 p-3 text-zinc-700"><UserPlus className="h-5 w-5" /></div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Invite user</p>
              <h2 className="mt-2 text-2xl font-semibold text-zinc-900">Add an office teammate</h2>
            </div>
          </div>

          <label className="block space-y-2">
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Full name</span>
            <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="users-invite-full-name-input" onChange={(event) => setInviteForm((current) => ({ ...current, full_name: event.target.value }))} required type="text" value={inviteForm.full_name} />
          </label>
          <label className="block space-y-2">
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Email</span>
            <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="users-invite-email-input" onChange={(event) => setInviteForm((current) => ({ ...current, email: event.target.value }))} required type="email" value={inviteForm.email} />
          </label>
          <label className="block space-y-2">
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Role</span>
            <select className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="users-invite-role-select" onChange={(event) => setInviteForm((current) => ({ ...current, role: event.target.value, permissions: ROLE_DEFAULT_PERMISSIONS[event.target.value] || [] }))} value={inviteForm.role}>
              <option value="Dispatcher">Dispatcher</option>
              <option value="Accounting">Accounting</option>
              <option value="Admin">Admin</option>
            </select>
          </label>

          {inviteForm.role === "Admin" ? (
            <p className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-500">Admins automatically get access to every section.</p>
          ) : (
            <div className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Access permissions</span>
              <PermissionsChecklist catalog={permissionCatalog} idPrefix="users-invite" onChange={(permissions) => setInviteForm((current) => ({ ...current, permissions }))} selected={inviteForm.permissions} />
            </div>
          )}

          <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70" data-testid="users-invite-submit-button" disabled={inviteSaving} type="submit">{inviteSaving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}Send invite</button>
        </form>

        <div className="panel-surface space-y-4 px-6 py-6" data-testid="users-management-panel">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Current team</p>
              <h2 className="mt-2 text-2xl font-semibold text-zinc-900">User access list</h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <input className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="users-search-input" onChange={(event) => setSearch(event.target.value)} placeholder="Search name or email" value={search} />
              <select className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="users-status-filter" onChange={(event) => setStatusFilter(event.target.value)} value={statusFilter}>
                <option value="all">All statuses</option>
                <option value="Active">Active</option>
                <option value="Invited">Invited</option>
                <option value="Disabled">Disabled</option>
              </select>
            </div>
          </div>

          <div className="table-shell" data-testid="users-table-shell">
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Access</th>
                    <th>Last sign in</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td className="py-10 text-center text-sm text-zinc-500" colSpan={5} data-testid="users-loading-state">Loading users...</td></tr>
                  ) : visibleUsers.length ? visibleUsers.map((user) => (
                    <tr data-testid={`users-row-${user.id}`} key={user.id}>
                      <td data-testid={`users-name-${user.id}`}>
                        <p className="font-semibold text-zinc-900">{user.name}</p>
                        <p className="mt-1 text-xs text-zinc-500">{user.email}</p>
                      </td>
                      <td>
                        <select className="w-full rounded-2xl border border-zinc-200 bg-white px-3 py-2 text-sm" data-testid={`users-role-select-${user.id}`} disabled={rowSavingId === user.id} onChange={(event) => handleRoleChange(user, event.target.value)} value={user.role}>
                          <option value="Dispatcher">Dispatcher</option>
                          <option value="Accounting">Accounting</option>
                          <option value="Admin">Admin</option>
                        </select>
                      </td>
                      <td>
                        <div className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] ${statusStyles[user.auth_status] || statusStyles.Active}`} data-testid={`users-status-${user.id}`}>{user.auth_status}</div>
                      </td>
                      <td data-testid={`users-last-sign-in-${user.id}`}>{formatDateTime(user.last_sign_in_at)}</td>
                      <td>
                        <div className="flex flex-wrap gap-2">
                          <button className="inline-flex items-center gap-2 rounded-full border border-zinc-200 px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-60" data-testid={`users-reset-email-button-${user.id}`} disabled={rowSavingId === user.id} onClick={() => handleSendReset(user)} type="button">
                            <Mail className="h-3.5 w-3.5" /> {user.auth_status === "Invited" ? "Resend setup" : "Reset password"}
                          </button>
                          <button className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-amber-700 transition hover:border-amber-300 disabled:cursor-not-allowed disabled:opacity-60" data-testid={`users-manual-link-button-${user.id}`} disabled={rowSavingId === user.id} onClick={() => handleGenerateManualLink(user)} type="button">
                            <Link2 className="h-3.5 w-3.5" /> {user.auth_status === "Invited" ? "Copy setup link" : "Copy reset link"}
                          </button>
                          {user.role !== "Admin" ? (
                            <button className="inline-flex items-center gap-2 rounded-full border border-zinc-200 px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-60" data-testid={`users-manage-access-button-${user.id}`} disabled={rowSavingId === user.id} onClick={() => openAccessDialog(user)} type="button">
                              <ShieldCheck className="h-3.5 w-3.5" /> Manage access
                            </button>
                          ) : null}
                          <button className="inline-flex items-center gap-2 rounded-full border border-zinc-200 px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-60" data-testid={`users-toggle-active-button-${user.id}`} disabled={rowSavingId === user.id || currentUser?.id === user.id} onClick={() => handleToggleActive(user)} type="button">{user.active_status ? "Disable" : "Enable"}</button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td className="py-10 text-center text-sm text-zinc-500" colSpan={5} data-testid="users-empty-state">No users match the current search.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <Dialog onOpenChange={(open) => setAccessDialog((current) => ({ ...current, open }))} open={accessDialog.open}>
        <DialogContent data-testid="users-access-dialog">
          <DialogHeader>
            <DialogTitle>Manage access for {accessDialog.user?.name}</DialogTitle>
          </DialogHeader>
          <PermissionsChecklist catalog={permissionCatalog} idPrefix="users-access-dialog" onChange={(permissions) => setAccessDialog((current) => ({ ...current, permissions }))} selected={accessDialog.permissions} />
          <DialogFooter>
            <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70" data-testid="users-access-dialog-save-button" disabled={rowSavingId === accessDialog.user?.id} onClick={handleSaveAccess} type="button">Save access</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {manualLinkState.link ? (
        <section className="panel-surface space-y-4 px-6 py-6" data-testid="users-manual-link-card">
          <div className="flex items-start gap-3">
            <div className="rounded-2xl bg-amber-50 p-3 text-amber-700"><Link2 className="h-5 w-5" /></div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Manual fallback</p>
              <h2 className="mt-2 text-2xl font-semibold text-zinc-900">{manualLinkState.label} ready for {manualLinkState.email}</h2>
            </div>
          </div>
          <label className="block space-y-2">
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Manual link</span>
            <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900" data-testid="users-manual-link-input" readOnly value={manualLinkState.link} />
          </label>
          <div className="flex flex-wrap gap-3">
            <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-4 py-3 text-sm font-semibold text-white" data-testid="users-manual-link-copy-button" onClick={handleCopyVisibleManualLink} type="button"><Copy className="h-4 w-4" /> Copy link</button>
            <a className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-700" data-testid="users-manual-link-open-button" href={manualLinkState.link} rel="noreferrer" target="_blank"><Link2 className="h-4 w-4" /> Open link</a>
          </div>
        </section>
      ) : null}
    </div>
  );
}